import { useMemo } from 'react';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import {
	useServiceCore,
	type ToResolvedService,
	type AnyService,
	type DependencyOverridesOf,
} from '@atlassian/expansion-services/core';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useAnalyticsEvents } from './services/analytics-events.tsx';
import { jiraFetchJson } from './services/fetch.tsx';
import { useJiraCurrentProduct } from './services/current-product.tsx';

const useEnvironmentDeps = () => {
	// Use product's hooks to build a bag of dependencies
	// that the product environment will provide to the services.
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const cloudId = useCloudId();
	const product = useJiraCurrentProduct();
	const atlassianAccountId = useAccountId();

	return useMemo(
		() => ({
			createAnalyticsEvent,
			fetchJSON: jiraFetchJson,
			getCurrentCloudId: () => cloudId,
			getCurrentProduct: () => product,
			getCurrentUserId: () => atlassianAccountId,
			fireErrorAnalytics,
		}),
		[createAnalyticsEvent, cloudId, product, atlassianAccountId],
	);
};
type EnvironmentDeps = ReturnType<typeof useEnvironmentDeps>;
export const useService = <DeclaredService extends AnyService>(
	service: DeclaredService,
	providedDeps?: DependencyOverridesOf<DeclaredService, EnvironmentDeps>,
): ToResolvedService<DeclaredService> => {
	const environmentDeps = useEnvironmentDeps();
	const deps = useMemo(
		() => ({ ...environmentDeps, ...providedDeps }),
		[environmentDeps, providedDeps],
	);

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	return useServiceCore(service, deps as never);
};
